import React, { useCallback, useMemo, useState } from 'react';
import { TableRow, TableCell, IconButton, FormControl, Checkbox, Hidden } from '@material-ui/core';
import { Link, useParams } from 'react-router-dom';
import useMedicalRecordFetch from 'api/MedicalRecordsFetch';
import Delete from '@material-ui/icons/Delete';
import { useStoreState } from 'easy-peasy';
import { UserTypeEnum } from 'types';
import getPermissions from 'utils/getPermissions';
import { roundTo2 } from 'utils/roundTo2';
import usePermissions from 'utils/usePermissions';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { useTranslations } from 'utils/useTranslations';
import { useStyles } from './styles';

const MedicalRecordSKUTableRow = ({ row, canBeResolved = false, onChangePrice, onResolveRequest = (data) => {} }) => {
  const styles = useStyles();
  const intl = useTranslations();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const user = useStoreState((state) => state.user);
  const { patient } = useParams<{ patient: string }>();
  const { updateAssignedCatalogItem, deleteCatalogItem } = useMedicalRecordFetch();
  const { canViewPrices, canCloseRequests, canDeleteSkusInMedicalRecord } = usePermissions();

  const handleStatusChange = useCallback(async () => {
    try {
      setStatusLoading(true);
      await updateAssignedCatalogItem({ id: row._id, isResolved: !row.resolved, medicalRecordId: row.medicalRecordId });
      setStatusLoading(false);
    } catch (err) {
      setStatusLoading(false);
    }
  }, [row]);

  const handleDelete = useCallback(async () => {
    try {
      setDeleteLoading(true);
      await deleteCatalogItem({ catalogItem: row, recordId: row.medicalRecordId });
      setDeleteLoading(false);
    } catch (err) {
      setDeleteLoading(false);
    }
  }, [row]);

  // const onResolveRequest = useCallback(() => {
  //   console.log({ id: row._id, recordId: row.medicalRecordId });
  //   // deleteCatalogItem({ id: row._id, recordId: row.medicalRecordId });
  // }, [row]);

  const canUnresolve = getPermissions(user.type, [UserTypeEnum.admin, UserTypeEnum.staff]);

  const canDelete = useMemo(() => canDeleteSkusInMedicalRecord(patient), [canDeleteSkusInMedicalRecord, patient]);

  const canChangePrice = getPermissions(user.type, [UserTypeEnum.admin, UserTypeEnum.staff]);

  const isRequest = row.subcategory === 'exam' && canCloseRequests;

  return (
    <>
      <Hidden smDown>
        <TableRow>
          <TableCell className={styles.tableText}>{row.codigo}</TableCell>
          <TableCell className={styles.tableText}>{row.descripcion}</TableCell>
          {canViewPrices && <TableCell className={styles.tableText}>{roundTo2(row.precio)}</TableCell>}
          <TableCell className={styles.tableText}>{row.amount}</TableCell>
          {canViewPrices && <TableCell className={styles.tableText}>{roundTo2(row.precio * row.amount)}</TableCell>}
          <TableCell className={styles.tableText}>{row.compania}</TableCell>
          <TableCell className={styles.tableText}>
            <Link to={`/user/medical_professionals/profile/${canBeResolved ? row.resolvedBy?._id : row.createdBy?._id}`}>
              {canBeResolved ? row.resolvedBy?.firstName : row.createdBy?.firstName}{' '}
              {canBeResolved ? row.resolvedBy?.lastName : row.createdBy?.lastName}
            </Link>
          </TableCell>
          {canBeResolved && <TableCell className={styles.tableText}>{row.resolvedAt}</TableCell>}
          {isRequest ? (
            <TableCell className={styles.tableText}>
              <FormControl
                disabled={row.resolved || statusLoading}
                checked={row.resolved}
                component={Checkbox}
                onClick={() => onResolveRequest({ catalogItemId: row._id, medicalRecordId: row.medicalRecordId })}
              />
            </TableCell>
          ) : (
            canBeResolved && (
              <TableCell className={styles.tableText}>
                <FormControl
                  disabled={(!canUnresolve && row.resolved) || statusLoading}
                  checked={row.resolved}
                  component={Checkbox}
                  onClick={handleStatusChange}
                />
              </TableCell>
            )
          )}

          <TableCell>
            {canDelete && (
              <IconButton disabled={deleteLoading} onClick={handleDelete} color="secondary">
                <Delete />
              </IconButton>
            )}

            {canBeResolved && onChangePrice && canChangePrice && (
              <IconButton onClick={onChangePrice} color="primary">
                <EditOutlinedIcon />
              </IconButton>
            )}
            {/* 
        {isRequest && (
          <IconButton
            disabled={row.resolvedAsRequest}
            onClick={() => onResolveRequest({ catalogItemId: row._id, medicalRecordId: row.medicalRecordId })}
            color="primary"
          >
            <DoneOutlineIcon />
          </IconButton>
        )} */}
          </TableCell>
        </TableRow>
      </Hidden>
      <Hidden mdUp>
        <TableRow className={styles.smDividerTop}>
          <TableCell>{intl('shared.code')}</TableCell>
          <TableCell className={styles.tableText}>{row.codigo}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>{intl('shared.description')}</TableCell>
          <TableCell className={styles.tableText}>{row.descripcion}</TableCell>
        </TableRow>
        {canViewPrices && (
          <TableRow>
            <TableCell>{intl('shared.price')}</TableCell>
            <TableCell className={styles.tableText}>{roundTo2(row.precio)}</TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell>{intl('shared.quantity')}</TableCell>
          <TableCell className={styles.tableText}>{row.amount}</TableCell>
        </TableRow>
        {canViewPrices && (
          <TableRow>
            <TableCell>{intl('shared.subtotal')}</TableCell>
            <TableCell className={styles.tableText}>{roundTo2(row.precio * row.amount)}</TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell>{intl('shared.company')}</TableCell>
          <TableCell className={styles.tableText}>{row.compania}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>{canBeResolved ? intl('shared.resolvedBy') : intl('shared.addedBy')}</TableCell>
          <TableCell className={styles.tableText}>
            <Link to={`/user/medical_professionals/profile/${canBeResolved ? row.resolvedBy?._id : row.createdBy?._id}`}>
              {canBeResolved ? row.resolvedBy?.firstName : row.createdBy?.firstName}{' '}
              {canBeResolved ? row.resolvedBy?.lastName : row.createdBy?.lastName}
            </Link>
          </TableCell>
        </TableRow>
        {canBeResolved && (
          <TableRow>
            <TableCell>{intl('shared.voucherNumber')}</TableCell>
            <TableCell className={styles.tableText}>{row.resolvedAt}</TableCell>
          </TableRow>
        )}
        {canBeResolved && (
          <TableRow>
            <TableCell>{intl('shared.status')}</TableCell>
            {isRequest ? (
              <TableCell className={styles.tableText}>
                <FormControl
                  disabled={row.resolved || statusLoading}
                  checked={row.resolved}
                  component={Checkbox}
                  onClick={() => onResolveRequest({ catalogItemId: row._id, medicalRecordId: row.medicalRecordId })}
                />
              </TableCell>
            ) : (
              canBeResolved && (
                <TableCell className={styles.tableText}>
                  <FormControl
                    disabled={(!canUnresolve && row.resolved) || statusLoading}
                    checked={row.resolved}
                    component={Checkbox}
                    onClick={handleStatusChange}
                  />
                </TableCell>
              )
            )}
          </TableRow>
        )}
        <TableRow className={styles.smDividerBottom}>
          <TableCell>
            {canDelete && (
              <IconButton disabled={deleteLoading} onClick={handleDelete} color="secondary">
                <Delete />
              </IconButton>
            )}
          </TableCell>
          <TableCell>
            {canBeResolved && onChangePrice && canChangePrice && (
              <IconButton onClick={onChangePrice} color="primary">
                <EditOutlinedIcon />
              </IconButton>
            )}
          </TableCell>
        </TableRow>
      </Hidden>
    </>
  );
};

export default MedicalRecordSKUTableRow;
